/* eslint-disable react/forbid-prop-types */
/* eslint-disable max-len */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useMemo, useState } from 'react';
import i18n from 'i18next';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { useModals } from 'core/notifications';

import { Loading } from 'uikit/loaders';
import { Pagination } from 'uikit/pages';

import {
  EnrollmentsDrawer,
  GenerateAdhocLettersModal,
  getBulkEnrollmentsModal,
  getMenuItems,
  ListEmpty,
  ListHeader,
  useGlobalQueries,
  useIsFeatureEnabled,
} from '@/exceed';
import { NestedRows, DraggableListItem } from '@/nestedRows';

import EnrollmentsListContent from './enrollmentsListContent';
import EnrollmentsSearch from './enrollmentsSearch';

import getAddUserEnrollmentsModal from '../helpers/services/getAddUserEnrollmentsModal';
import getSelectedUsersCount from '../helpers/services/getSelectedUsersCount';
import getEditEnrollmentModal from '../helpers/services/getEditEnrollmentModal';
import getMaximumEnrollmentsBreachedModal from '../helpers/services/getMaximumEnrollmentsBreachedModal';
import getEnrollmentsMaximum from '../helpers/getEnrollmentsMaximum';

import useEnrolledUsers from '../hooks/useEnrolledUsers';

import styles from '../styles/enrollments.module.scss';

const Enrollments = ({
  courseId,
  courseTitle = '',
  hasFixedHeader = false,
  isModalVariant = false,
  listHeaderButtons = [],
  statusOptions = [],
}) => {
  const { addModal } = useModals();
  const { currentUser } = useGlobalQueries();
  const [isEnrollmentsDrawerActive, setIsEnrollmentsDrawerActive] = useState(false);

  const {
    enrollmentsGroups,
    enrollmentsFilters,
    enrolledUsers,
    enrolledUsersQuery,
    setEnrolledUsersQuery,
    removeEnrolledUsersQuery,
    enrollmentsSelectedUsers,
    setEnrollmentsSelectedUsers,
    resetEnrollmentsSelectedUsers,
    removeEnrollmentsSelectedUsers,
  } = useEnrolledUsers({
    courseId,
    isModalVariant,
  });

  useEffect(
    () => () => {
      if (!isModalVariant) {
        // must be remove instead of reset to avoid updating state on an unmounted component
        removeEnrollmentsSelectedUsers();
        removeEnrolledUsersQuery();
      }
    },
    [removeEnrollmentsSelectedUsers, removeEnrolledUsersQuery, isModalVariant],
  );

  const statuses = statusOptions.length ? statusOptions : enrolledUsers.data.meta.statuses;
  const hasAdditionalFeatures = useIsFeatureEnabled('videos_additional_features');

  const hasActiveSearch = useMemo(() => {
    const { filters } = enrolledUsersQuery;
    const filtersKey = isModalVariant ? 'user' : 'enrollment';
    return Object.keys(filters[filtersKey]).length > 0 || filters.user.query.length > 0;
  }, [enrolledUsersQuery, isModalVariant]);

  const handleToggleEnrollmentsDrawer = (state) => {
    setIsEnrollmentsDrawerActive(state);
  };

  const getSingleSelectedUser = () => {
    const selectedUser = Object.keys(enrollmentsSelectedUsers.data.users)[0];
    if (selectedUser) return Number(selectedUser);
  };

  const getListHeaderTitle = () => {
    const { count } = enrolledUsers.data.meta;
    const fallbackTitle = isModalVariant ? i18n.t('users') : i18n.t('enrollments');
    return hasActiveSearch
      ? i18n.t('results-count', { count: count.toLocaleString(document.documentElement.lang) })
      : fallbackTitle;
  };

  const getListItemCheckBoxValue = (id) =>
    enrollmentsSelectedUsers.data.headerCheckboxStatus === 'checked' ||
    enrollmentsSelectedUsers.data.users?.[id];

  const getNoticeButtonText = () => {
    const {
      meta: { count },
    } = enrolledUsers.data;
    const { hasSelectedAllUsers } = enrollmentsSelectedUsers.data;
    return hasSelectedAllUsers
      ? i18n.t('clear-selection')
      : i18n.t('select-all-users', { count: count.toLocaleString(document.documentElement.lang) });
  };

  const getNoticeText = () => {
    const { count: enrolledUsersCount } = enrolledUsers.data.meta;
    const { hasSelectedAllUsers } = enrollmentsSelectedUsers.data;
    const selectedUsersCount = getSelectedUsersCount({ enrollmentsSelectedUsers });
    const count = hasSelectedAllUsers ? enrolledUsersCount : selectedUsersCount;
    return i18n.t('all-users-on-page-selected', {
      count: count.toLocaleString(document.documentElement.lang),
    });
  };

  const getListHeaderAddMenuItems = () => {
    if (isModalVariant) return [];

    const { difference, maximum, shouldEnforceMaximum } = getEnrollmentsMaximum({
      enrolledUsers,
      enrollmentsSelectedUsers,
    });
    const shouldShowMaximumEnrollmentsBreachedModal =
      shouldEnforceMaximum && maximum && difference >= 0;

    return getMenuItems([
      {
        icon: 'enrollment',
        key: 'enroll-users',
        onClick: () =>
          shouldShowMaximumEnrollmentsBreachedModal
            ? getMaximumEnrollmentsBreachedModal({ maximum })
            : getAddUserEnrollmentsModal({
                courseId,
                courseTitle,
                resetBaseSelectedUsers: resetEnrollmentsSelectedUsers,
                statuses,
                baseEnrolledUsers: enrolledUsers,
              }),
      },
      {
        icon: 'upload',
        key: 'import',
        onClick: () =>
          addModal(
            getBulkEnrollmentsModal({
              course_id: courseId,
              time_zone: currentUser.data.tzid,
              type: 'enrollment_course_session',
            }),
          ),
      },
    ]);
  };

  const onHandleAdhocModal = () => {
    const selectedUsersCount = getSelectedUsersCount({ enrollmentsSelectedUsers });

    addModal({
      body: i18n.t('generate-adhoc-letters-body', { count: selectedUsersCount }),
      component: (
        <GenerateAdhocLettersModal
          courseId={courseId}
          enrollmentIds={[...Object.keys(enrollmentsSelectedUsers.data.users).map(Number)]}
        />
      ),
      hasCloseButton: true,
      title: i18n.t('generate-adhoc-letters-title'),
    });
  };

  const getListHeaderButtons = () => {
    if (isModalVariant && listHeaderButtons?.length) return listHeaderButtons;

    const variant = 'outlined';
    const size = 'small';
    const selectedUsersCount = getSelectedUsersCount({ enrollmentsSelectedUsers });

    const buttons = [
      {
        text: i18n.t('edit'),
        icon: 'pencil',
        onClick: () =>
          getEditEnrollmentModal({
            courseId,
            enrolledUsers,
            enrollmentsSelectedUsers,
            resetEnrollmentsSelectedUsers,
            selectedUsersCount,
          }),
        variant,
        size,
      },
    ];

    if (selectedUsersCount === 1) {
      buttons.push({
        text: i18n.t('show'),
        icon: 'visible',
        onClick: () => handleToggleEnrollmentsDrawer(true),
        variant,
        size,
      });
    }

    /* buttons.push({
      text: i18n.t('delete'),
      icon: 'delete',
      onClick: () => {},
      variant,
      size,
    }); */

    return buttons;
  };

  const getListHeaderColumns = () =>
    isModalVariant ? [] : [i18n.t('enrolled-on'), i18n.t('status'), i18n.t('version')];

  const getListMoreMenuItems = () =>
    getMenuItems([
      { key: 'letter', icon: 'email', onClick: () => onHandleAdhocModal() },
      ...(hasAdditionalFeatures
        ? [
            { key: 'announcements', icon: 'announcement', onClick: () => {} },
            { key: 'copy-email-addresses', icon: 'emailAddress', onClick: () => {} },
          ]
        : []),
    ]);

  const getEmptyStateTitle = () => {
    if (hasActiveSearch) return i18n.t('no-results-found');

    return isModalVariant ? i18n.t('no-users-found-title') : i18n.t('no-users-enrolled-title');
  };

  const getEmptyStateBody = () => {
    if (hasActiveSearch) return i18n.t('modify-your-search');

    return isModalVariant ? '' : i18n.t('no-users-enrolled-body');
  };

  const shouldShowLoadingWheel = () =>
    enrolledUsers.isFetching || (isModalVariant && enrollmentsGroups.isFetching);

  const shouldShowListView = () =>
    Boolean(enrolledUsers.data.enrollments.items.root?.children.length) &&
    !shouldShowLoadingWheel();

  const shouldShowPagination = () => enrolledUsers.data.meta.count > 50;

  const shouldShowEmptyState = () => !enrolledUsers.data.meta.count && !shouldShowLoadingWheel();

  const shouldShowHeaderNotice = () =>
    enrollmentsSelectedUsers.data.headerCheckboxStatus === 'checked' &&
    enrolledUsers.data.meta.count > 50;

  const shouldShowHeaderButtons = () =>
    Boolean(getSelectedUsersCount({ enrollmentsSelectedUsers }));

  const shouldShowMoreMenu = () => shouldShowHeaderButtons() && !isModalVariant;

  const shouldShowHeaderAddMenu = () => !isModalVariant && !hasActiveSearch;

  const shouldDisableCheckbox = () =>
    enrolledUsers.data.meta.count === 0 || shouldShowLoadingWheel();

  const onHeaderCheckboxChange = () => {
    if (['checked', 'indeterminate'].includes(enrollmentsSelectedUsers.data.headerCheckboxStatus)) {
      resetEnrollmentsSelectedUsers();
    } else {
      const {
        data: {
          enrollments: {
            items: {
              root: { children: allUserIds },
            },
          },
        },
      } = enrolledUsers;

      const allUsers = allUserIds.reduce((acc, userId) => {
        acc[userId] = true;
        return acc;
      }, {});

      handleToggleEnrollmentsDrawer(false);

      setEnrollmentsSelectedUsers((oldSelectedUsers) => ({
        ...oldSelectedUsers,
        users: allUsers,
        count: allUserIds.length,
        headerCheckboxStatus: 'checked',
      }));
    }
  };

  const onListItemCheckboxChange = ({ value, id }) => {
    const copyOfEnrollmentsSelectedUsers = { ...enrollmentsSelectedUsers.data.users };

    if (value) {
      copyOfEnrollmentsSelectedUsers[id] = true;
    } else {
      delete copyOfEnrollmentsSelectedUsers[id];
    }

    const selectedUserIds = Object.keys(copyOfEnrollmentsSelectedUsers);

    const {
      data: {
        meta: { count },
      },
    } = enrolledUsers;

    let headerCheckboxStatus = '';

    switch (selectedUserIds.length) {
      case 0:
        headerCheckboxStatus = 'unchecked';
        break;
      case Math.min(count, 50):
        headerCheckboxStatus = 'checked';
        break;
      default:
        headerCheckboxStatus = 'indeterminate';
    }

    handleToggleEnrollmentsDrawer(false);

    setEnrollmentsSelectedUsers((oldSelectedUsers) => ({
      ...oldSelectedUsers,
      users: copyOfEnrollmentsSelectedUsers,
      count: selectedUserIds.length,
      hasSelectedAllUsers:
        value === false ? false : enrollmentsSelectedUsers.data.hasSelectedAllUsers,
      headerCheckboxStatus,
    }));
  };

  const onPaginationButtonClick = (page) => {
    resetEnrollmentsSelectedUsers();
    setEnrolledUsersQuery((oldQuery) => ({
      ...oldQuery,
      pagination_params: { ...enrolledUsersQuery.pagination_params, page },
    }));
  };

  const onNoticeButtonClick = () => {
    const {
      data: { hasSelectedAllUsers },
    } = enrollmentsSelectedUsers;
    if (!hasSelectedAllUsers) {
      return setEnrollmentsSelectedUsers((oldSelectedUsers) => ({
        ...oldSelectedUsers,
        hasSelectedAllUsers: true,
        count: enrolledUsers.data.meta.count,
      }));
    }
    resetEnrollmentsSelectedUsers();
  };

  const {
    pagination_params: { page: currentPage },
  } = enrolledUsersQuery;
  const {
    enrollments,
    meta: { count },
    pagination: { total_pages: totalPages },
  } = enrolledUsers.data;

  return (
    <div
      className={classnames(styles.element, { [styles.hasFixedHeader]: hasFixedHeader })}
      data-testid="enrollments"
    >
      <EnrollmentsSearch
        enrollmentsFilters={enrollmentsFilters}
        enrolledUsersQuery={enrolledUsersQuery}
        setEnrolledUsersQuery={setEnrolledUsersQuery}
        isModalVariant={isModalVariant}
        resetEnrollmentsSelectedUsers={resetEnrollmentsSelectedUsers}
      />
      <ListHeader
        className={styles.listHeader}
        columns={getListHeaderColumns()}
        addMenuItems={getListHeaderAddMenuItems()}
        hasAddMenu={shouldShowHeaderAddMenu()}
        title={getListHeaderTitle()}
        nameText={i18n.t('user')}
        hasButtons={shouldShowHeaderButtons()}
        buttons={getListHeaderButtons()}
        hasCheckbox
        checkboxValue={enrollmentsSelectedUsers.data.headerCheckboxStatus === 'checked'}
        isCheckboxIndeterminate={
          enrollmentsSelectedUsers.data.headerCheckboxStatus === 'indeterminate'
        }
        isCheckboxDisabled={shouldDisableCheckbox()}
        checkboxInputAriaLabel={i18n.t('select-all-users', { count })}
        onCheckboxChange={onHeaderCheckboxChange}
        hasNotice={shouldShowHeaderNotice()}
        noticeText={getNoticeText()}
        noticeButtonText={getNoticeButtonText()}
        noticeOnClick={onNoticeButtonClick}
        nameMetaText={i18n.t('user-count-returned', {
          count: count.toLocaleString(document.documentElement.lang),
        })}
        hasMoreMenu={shouldShowMoreMenu()}
        moreMenuItems={getListMoreMenuItems()}
      />
      <div className={styles.content}>
        <div className={styles.scrollableContent}>
          {shouldShowLoadingWheel() && (
            <div className={styles.loading}>
              <Loading />
            </div>
          )}
          {shouldShowListView() && (
            <div className={styles.rows}>
              <NestedRows
                data={enrollments}
                shouldPreventTransform
                containerHeight="auto"
                isDragEnabled={false}
                isNestingEnabled={false}
                renderComponent={(props) => (
                  <DraggableListItem
                    {...props}
                    // eslint-disable-next-line react/prop-types
                    checkboxValue={getListItemCheckBoxValue(props.item.id)}
                    onActionItemClick={() => {}}
                    onKebabMenuItemClick={() => {}}
                    onListItemCheckboxChange={onListItemCheckboxChange}
                    components={{
                      Actions: () => null,
                      DragHandle: () => null,
                      KebabMenu: () => null,
                      Content: EnrollmentsListContent,
                      ToggleExpandButton: () => null,
                    }}
                  />
                )}
              />
            </div>
          )}
          {shouldShowEmptyState() && (
            <ListEmpty
              title={getEmptyStateTitle()}
              body={getEmptyStateBody()}
              hasButton={!isModalVariant}
              buttonText={i18n.t('enroll-users')}
              onButtonClick={() =>
                getAddUserEnrollmentsModal({
                  courseId,
                  courseTitle,
                  resetBaseSelectedUsers: resetEnrollmentsSelectedUsers,
                  statuses,
                  baseEnrolledUsers: enrolledUsers,
                })
              }
            />
          )}
        </div>
        {shouldShowPagination() && (
          <div className={styles.pagination}>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageClick={onPaginationButtonClick}
            />
          </div>
        )}
        <EnrollmentsDrawer
          enrollmentId={getSingleSelectedUser()}
          onToggleEnrollmentsDrawer={() => handleToggleEnrollmentsDrawer(false)}
          isEnrollmentsDrawerActive={isEnrollmentsDrawerActive && !!getSingleSelectedUser()}
        />
      </div>
    </div>
  );
};

Enrollments.propTypes = {
  courseId: PropTypes.number.isRequired,
  courseTitle: PropTypes.string,
  hasFixedHeader: PropTypes.bool,
  isModalVariant: PropTypes.bool,
  listHeaderButtons: PropTypes.arrayOf(PropTypes.shape({})),
};

export default Enrollments;
